// Extract URL parameters
const urlParams = new URLSearchParams(window.location.search);
const bundleId = urlParams.get('bundleId');
const revisionId = urlParams.get('revisionId');

const API_BASE_URL = 'https://api.labeldesign.ai';
const getProjectDataUrl = `${API_BASE_URL}/getprojectdata`;
const updateApprovalUrl = `${API_BASE_URL}/updateprojectstatus`;
const fetchShopifyOrderDataUrl = `${API_BASE_URL}/fetchshopifyorderdata`;
const sendKlaviyoEventUrl = `${API_BASE_URL}/sendklaviyoevent`;

// Initialize a flag to prevent duplicate initialization
window.proofReviewInitialized = window.proofReviewInitialized || false;

// Function to show/hide the loading spinner
const toggleLoadingSpinner = (show) => {
    const loadingSpinner = document.getElementById('loading-spinner');
    const proofReviewUI = document.getElementById('proof-review');
    const downloadUI = document.getElementById('proof-review-download');
    
    if (loadingSpinner) {
        if (show) {
            loadingSpinner.style.display = 'flex';
            if (proofReviewUI) proofReviewUI.style.display = 'none';
            if (downloadUI) downloadUI.style.display = 'none';
        } else {
            loadingSpinner.style.display = 'none';
        }
    }
};

// Function to fetch project data from Firebase Functions
const fetchProjectData = async () => {
    // Show loading spinner
    toggleLoadingSpinner(true);
    
    // Show error message if bundleId is missing
    if (!bundleId) {
        toggleLoadingSpinner(false);
        showErrorMessage('Missing project ID. Please check the URL and try again.');
        return;
    }

    try {
        const response = await fetch(getProjectDataUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                bundleId: bundleId,
                include: 'revisionFiles'
            })
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();

        // Check if data is valid and has the expected structure
        if (!data || !data.bundleId) {
            toggleLoadingSpinner(false);
            showErrorMessage('Project not found. Please check the URL and try again.');
            return null;
        }
        
        // If we have a Shopify order ID, fetch customer data
        if (data.shopify_order_id) {

            // Fetch customer data in the background - don't await to avoid blocking UI updates
            fetchCustomerData(data.shopify_order_id)
                .then(customerData => {
                    if (customerData) {
                        // Update the customer info section with the fetched data
                        updateCustomerInfo(customerData);
                    }
                })
                .catch(error => {});
        }

        // Since both UI update functions are now async, we need to await them
        await updateProofReviewUI(data);
        await updateDownloadUI(data);
        
        // Hide loading spinner only after both UI updates are complete
        toggleLoadingSpinner(false);

        return data;
    } catch (error) {
        toggleLoadingSpinner(false);
        showErrorMessage('We couldn\'t load your project at this time. Please try again later or contact support.');
        return null;
    }
};

/**
 * Function to fetch customer data from Shopify using Firebase Function
 * @param {string} orderId - The Shopify order ID (can be in format 'gid://shopify/Order/1234567890' or just '1234567890')
 * @returns {Promise<Object|null>} - Customer data object or null if error
 */
const fetchCustomerData = async (orderId) => {
    if (!orderId) {
        return null;
    }
    
    try {
        // Handle different order ID formats
        let formattedOrderId;
        
        if (typeof orderId !== 'string') {
            // Convert to string if it's not already
            formattedOrderId = String(orderId);
        } else if (orderId.includes('gid://shopify/Order/')) {
            // Already in GID format, use as is
            formattedOrderId = orderId;
        } else if (orderId.includes('gid://')) {
            // Some other GID format, use as is
            formattedOrderId = orderId;
        } else {
            // Assume it's a numeric ID or contains a numeric ID
            // Extract only numeric characters
            const numericId = orderId.replace(/\D/g, '');
            if (!numericId) {
                return null; // No numeric ID found
            }
            formattedOrderId = numericId;
        }
        
        // Make the API request
        const response = await fetch(fetchShopifyOrderDataUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ orderId: formattedOrderId })
        });

        // Handle non-OK responses
        if (!response.ok) {
            return null;
        }
        
        // Parse response
        let data;
        try {
            const responseText = await response.text();
            data = JSON.parse(responseText);
        } catch (parseError) {
            return null;
        }
        
        // Validate response data
        if (!data || !data.success || !data.order) {
            return null;
        }
        
        // Cache the customer data for later use (e.g., Klaviyo event tracking)
        window.customerData = data.order;
        
        return data.order;
    } catch (error) {
        return null;
    }
};

// Function to update customer information in the UI
const updateCustomerInfo = (customerData) => {
    if (!customerData) return;
    
    // Find the customer info list items
    const customerInfoList = document.querySelector('.details-list:nth-of-type(2)');
    if (!customerInfoList) return;
    
    // Get the list items (skipping the header)
    const listItems = customerInfoList.querySelectorAll('li');
    if (listItems.length < 4) return; // Need at least 4 items (header + 3 customer info items)
    
    // Extract customer data with fallbacks for different data structures
    // Some Shopify responses might have customer at root level or nested
    const customer = customerData.customer || {};
    const orderName = customerData.name || '';
    const orderEmail = customerData.email || '';
    
    // Extract customer details with fallbacks
    const firstName = customer.firstName || '';
    const lastName = customer.lastName || '';
    const customerEmail = customer.email || '';
    const phone = customer.phone || '';
    
    // Format customer name with fallbacks
    let customerName = 'N/A';
    if (firstName || lastName) {
        customerName = `${firstName} ${lastName}`.trim();
    } else if (orderName) {
        // Try to extract name from order name if available
        customerName = orderName;
    }
    
    // Format phone number or use N/A if not available
    const phoneNumber = phone;
    
    // Use customer email or order email with fallback
    const emailAddress = customerEmail || orderEmail;
    
    // Update the individual list items directly instead of replacing the entire HTML
    // This is more efficient and prevents any flickering
    listItems[1].textContent = customerName;
    listItems[2].textContent = phoneNumber;
    listItems[3].textContent = emailAddress;
};

// Function to show error message when bundleId is invalid or missing
const showErrorMessage = (message) => {
    const proofReviewSection = document.querySelector('.section-proof-review');
    const proofReviewUI = document.getElementById('proof-review');
    const downloadUI = document.getElementById('proof-review-download');

    if (proofReviewSection) {
        // Add error class to the section
        proofReviewSection.classList.add('proof-review--error');

        // Create error message container
        const errorContainer = document.createElement('div');
        errorContainer.className = 'proof-review__error';
        errorContainer.innerHTML = `
            <header class="proof-review__header">
                <div class="proof-review__title">
                    <h1>Error</h1>
                </div>
                <div class="proof-review__description">
                    ${message}
                </div>
                <div class="proof-review__cta">
                    <a href="${window.location.pathname}" class="btn btn-large btn-primary">Refresh Page</a>
                </div>
            </header>
        `;

        // Hide existing UI elements
        if (proofReviewUI) proofReviewUI.style.display = 'none';
        if (downloadUI) downloadUI.style.display = 'none';

        // Add error container to the section
        proofReviewSection.appendChild(errorContainer);
    }
};

const updateProofReviewUI = async (data) => {
    if (!data) return;
    /* Variables */
    const orderNumber = data.order_name.split('#')[1];
    const bundleId = data.bundleId;
    
    // Initialize customer data variables with empty strings
    let customerName = '';
    let phoneNumber = '';
    let emailAddress = '';
    
    // Fetch customer data if we have an order ID
    if (data.shopify_order_id) {
        try {
            // Check if we already have customer data cached
            if (!window.customerData) {
                // Fetch customer data
                window.customerData = await fetchCustomerData(data.shopify_order_id);
            }
            
            if (window.customerData) {
                // Extract customer data with fallbacks for different data structures
                const customer = window.customerData.customer || {};
                const orderName = window.customerData.name || '';
                const orderEmail = window.customerData.email || '';
                
                // Extract customer details with fallbacks
                const firstName = customer.firstName || '';
                const lastName = customer.lastName || '';
                
                // Format customer name with fallbacks
                if (firstName || lastName) {
                    customerName = `${firstName} ${lastName}`.trim();
                } else if (orderName) {
                    customerName = orderName;
                }
                
                // Get phone and email
                phoneNumber = customer.phone || window.customerData.phone;
                emailAddress = customer.email || orderEmail;
            }
        } catch (error) {
            console.error('Error fetching customer data:', error);
        }
    }

    // Check if the current revision is approved
    let isApproved = false;

    // Check if any of the revision files have isApproved set to true
    if (data.revisionFiles && Array.isArray(data.revisionFiles)) {
        const currentRevisionFiles = data.revisionFiles.filter(file => file.revision_id === revisionId);

        if (currentRevisionFiles.length > 0) {
            // Check if any of the files has revisionData.isApproved set to true
            isApproved = currentRevisionFiles.some(file => file.revisionData && file.revisionData.isApproved === true);
        }
    }

    // Get the proof review and download sections
    const proofReviewSection = document.querySelector('.section-proof-review');
    const proofReviewUI = document.getElementById('proof-review');
    const downloadUI = document.getElementById('proof-review-download');

    // Handle UI changes based on approval status
    if (isApproved) {
        // Add is-approved class to the section
        if (proofReviewSection) {
            proofReviewSection.classList.add('is-approved');
        }

        // Show download UI and hide proof review UI for approved revisions
        if (proofReviewUI && downloadUI) {
            proofReviewUI.style.display = 'none';
            downloadUI.style.display = 'flex';
            downloadUI.style.opacity = '1';
        }
    } else {
        // For non-approved revisions, ensure proof review UI is shown
        if (proofReviewUI && downloadUI) {
            proofReviewUI.style.display = 'grid';
            proofReviewUI.style.opacity = '1';
            downloadUI.style.display = 'none';
            downloadUI.style.opacity = '0';
        }
    }

    /* Elements */
    const requestChangeBtn = document.getElementById('requestChangeBtn');
    const projectDetails = document.getElementById('projectDetails');

    /* Request Change Button */
    if (requestChangeBtn) {
        const baseUrl = requestChangeBtn.getAttribute('href').split('?')[0];
        const projectName = data.formData.summary.projectName;
        
        // Start with required parameters
        let newHref = `${baseUrl}?project=${bundleId}&orderNumber=${orderNumber}`;
        
        // Add project name if it exists
        if (projectName) {
            newHref += `&projectName=${encodeURIComponent(projectName)}`;
        }
        
        // Only add phone number if it exists and is not undefined
        if (phoneNumber) {
            newHref += `&phoneNumber=${encodeURIComponent(phoneNumber)}`;
        }
        
        // Only add email address if it exists and is not undefined
        if (emailAddress) {
            newHref += `&emailAddress=${encodeURIComponent(emailAddress)}`;
        }
        
        requestChangeBtn.setAttribute('href', newHref);
    }

    /* Project Details */
    if (projectDetails && data.formData) {
        const projectName = data.formData.summary.projectName;
        const editItems = data.formData.edit.editItems;
        const editItemsCount = editItems.length;

        // Add null checks for design properties
        let size, shape, designType, designDescription;
        try {
            if (data.formData.design && data.formData.design.size) {
                size = data.formData.design.size.displayName;
            }
            if (data.formData.design && data.formData.design.shape) {
                shape = data.formData.design.shape.displayName;
            }
            if (data.formData.designType) {
                designType = data.formData.designType.title;
            }
            if (data.formData.design.designDescription) {
                designDescription = data.formData.design.designDescription;
            }
        } catch (error) {}

        // Generate the complete project details HTML
        const projectDetailsHTML = `
            <ul class="details-list">
                <li>
                    <strong>Project:</strong>
                </li>
                <li>
                    ${projectName}
                </li>
                <li>
                    Order No. ${orderNumber}
                </li>
                ${editItemsCount ? `
                <li>
                    ${editItemsCount} edits requested |
                    <button type="button" class="btn-link" id="viewEditsToggle">View edits</button>
                </li>
                ` : ''}
                ${size ? `
                    <li>
                        ${size} ${shape.toLowerCase()} ${designType.toLowerCase()}
                        <button type="button" class="btn-link" id="viewDetailsToggle">View details</button>
                    </li>
                    ` : ''}
            </ul>
            <ul class="details-list">
                <li>
                    <strong>Proof for:</strong>
                </li>
                ${customerName ? `<li>${customerName}</li>` : ''}
                ${phoneNumber ? `<li>${phoneNumber}</li>` : ''}
                ${emailAddress ? `<li>${emailAddress}</li>` : ''}
            </ul>
        `;

        // Set the HTML content
        projectDetails.innerHTML = projectDetailsHTML;

        // Add drawer functionality for the edit items list
        const viewEditsToggle = document.getElementById('viewEditsToggle');
        const viewDetailsToggle = document.getElementById('viewDetailsToggle');

        // Handle edit items drawer
        if (viewEditsToggle && editItems.length > 0) {
            // Create a unique ID for this drawer to avoid conflicts
            const drawerId = 'editsDrawer_' + bundleId;

            // Remove any existing drawer with the same ID to prevent duplicates
            const existingDrawer = document.getElementById(drawerId);
            if (existingDrawer) {
                existingDrawer.remove();
            }

            // Create the drawer structure
            const editsDrawer = document.createElement('div');
            editsDrawer.id = drawerId;
            editsDrawer.className = 'proof-review__drawer';

            // Create drawer HTML
            editsDrawer.innerHTML = `
                <div class="drawer__overlay"></div>
                <div class="drawer__container" role="dialog" aria-modal="true" aria-labelledby="edits-drawer-title">
                    <div class="drawer__header">
                        <h2 id="edits-drawer-title" class="drawer__title">Requested Edits</h2>
                        <button class="drawer__close-button" aria-label="Close drawer">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="black"/>
                            </svg>
                        </button>
                    </div>
                    <div class="drawer__content">
                    ${editItems.map((item, index) => `
                            <h3>${index + 1}. ${item.type}</h3>
                            <p>${item.description}</p>
                    `).join('')}
                    </div>
                </div>
            `;

            // Append the drawer to the body
            document.body.appendChild(editsDrawer);

            // Define close drawer function
            const closeEditsDrawer = () => {
                editsDrawer.classList.remove('drawer--visible');
                document.body.style.overflow = '';
            };

            // Add event listeners for the drawer
            const closeDrawerBtn = editsDrawer.querySelector('.drawer__close-button');
            const drawerOverlay = editsDrawer.querySelector('.drawer__overlay');
            const drawerContainer = editsDrawer.querySelector('.drawer__container');

            // Event listeners
            closeDrawerBtn.addEventListener('click', closeEditsDrawer);
            drawerOverlay.addEventListener('click', closeEditsDrawer);

            // Close on escape key
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && editsDrawer.classList.contains('drawer--visible')) {
                    closeEditsDrawer();
                }
            });

            // Prevent clicks inside the drawer from closing it
            drawerContainer.addEventListener('click', (e) => {
                e.stopPropagation();
            });

            // Clear any existing event listeners using cloneNode
            const newViewEditsToggle = viewEditsToggle.cloneNode(true);
            viewEditsToggle.parentNode.replaceChild(newViewEditsToggle, viewEditsToggle);

            // Add click handler to toggle button
            newViewEditsToggle.addEventListener('click', (e) => {
                e.preventDefault();
                editsDrawer.classList.add('drawer--visible');
                document.body.style.overflow = 'hidden';
            });
        }

        // Handle design details drawer
        if (viewDetailsToggle && size) {
            // Create a unique ID for this drawer to avoid conflicts
            const detailsDrawerId = 'detailsDrawer_' + bundleId;

            // Remove any existing drawer with the same ID to prevent duplicates
            const existingDetailsDrawer = document.getElementById(detailsDrawerId);
            if (existingDetailsDrawer) {
                existingDetailsDrawer.remove();
            }

            // Create the drawer structure
            const detailsDrawer = document.createElement('div');
            detailsDrawer.id = detailsDrawerId;
            detailsDrawer.className = 'proof-review__drawer';

            // Create drawer HTML with design details
            detailsDrawer.innerHTML = `
                <div class="drawer__overlay"></div>
                <div class="drawer__container" role="dialog" aria-modal="true" aria-labelledby="details-drawer-title">
                    <div class="drawer__header">
                        <h2 id="details-drawer-title" class="drawer__title">Design Details</h2>
                        <button class="drawer__close-button" aria-label="Close drawer">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="black"/>
                            </svg>
                        </button>
                    </div>
                    <div class="drawer__content">
                        <p>${designDescription}</p>
                        <ul>
                            <li><strong>Size:</strong> ${size}</li>
                            <li><strong>Shape:</strong> ${shape}</li>
                            <li><strong>Product Type:</strong> ${designType}</li>
                        </ul>
                    </div>
                </div>
            `;

            // Append the drawer to the body
            document.body.appendChild(detailsDrawer);

            // Define close drawer function
            const closeDetailsDrawer = () => {
                detailsDrawer.classList.remove('drawer--visible');
                document.body.style.overflow = '';
            };

            // Add event listeners for the drawer
            const closeDrawerBtn = detailsDrawer.querySelector('.drawer__close-button');
            const drawerOverlay = detailsDrawer.querySelector('.drawer__overlay');
            const drawerContainer = detailsDrawer.querySelector('.drawer__container');

            // Event listeners
            closeDrawerBtn.addEventListener('click', closeDetailsDrawer);
            drawerOverlay.addEventListener('click', closeDetailsDrawer);

            // Close on escape key
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && detailsDrawer.classList.contains('drawer--visible')) {
                    closeDetailsDrawer();
                }
            });

            // Prevent clicks inside the drawer from closing it
            drawerContainer.addEventListener('click', (e) => {
                e.stopPropagation();
            });

            // Clear any existing event listeners using cloneNode
            const newViewDetailsToggle = viewDetailsToggle.cloneNode(true);
            viewDetailsToggle.parentNode.replaceChild(newViewDetailsToggle, viewDetailsToggle);

            // Add click handler to toggle button
            newViewDetailsToggle.addEventListener('click', (e) => {
                e.preventDefault();
                detailsDrawer.classList.add('drawer--visible');
                document.body.style.overflow = 'hidden';
            });
        }
    }

    /* Proofs */
    const proofsContainer = document.getElementById('proofsContainer');

    if (proofsContainer && data.revisionFiles && revisionId) {
        // Filter revision files to get only proof files for the current revision
        const proofFiles = data.revisionFiles.filter(file =>
            file.revision_id === revisionId && file.file_type === 'proof'
        );

        if (proofFiles.length > 0) {
            // Get the first proof file
            const proofFile = proofFiles[0];
            
            // Check if the file is an image based on its name or type
            const isImage = (fileName, fileType) => {
                const imageExtensions = ['jpg', 'jpeg', 'png'];
                const extension = fileName ? fileName.split('.').pop().toLowerCase() : '';
                const mimeType = fileType ? fileType.toLowerCase() : '';
                
                return imageExtensions.includes(extension) || 
                       mimeType.includes('image/jpeg') || 
                       mimeType.includes('image/png') || 
                       mimeType.includes('image/jpg');
            };
            
            // Determine if this proof file is an image
            const fileIsImage = isImage(proofFile.name || proofFile.fileName, proofFile.type || proofFile.fileType);
            
            // Create HTML for the proofs section based on file type
            let proofsHTML;
            if (fileIsImage) {
                proofsHTML = `
                    <div class="edits__preview edits__preview--image">
                        <img
                            src="${proofFile.url}"
                            alt="Proof Preview"
                            id="proofPreviewImage"
                        />
                    </div>
                `;
            } else {
                proofsHTML = `
                    <div class="edits__preview edits__preview--iframe">
                        <iframe
                            src="${proofFile.url}"
                            width="100%"
                            height="100%"
                            frameborder="0"
                            id="proofPreviewFrame"
                        ></iframe>
                    </div>
                `;
            }

            // Set the HTML content
            proofsContainer.innerHTML = proofsHTML;
            
        } else {
            proofsContainer.style.gridTemplateColumns = '1fr';
            proofsContainer.innerHTML = `
            <div class="no-proofs-message">
            <p>No proof files available for this revision. <br> Please check your email or contact with us.</p>
            </div>
            `;
            proofReviewSection.classList.add('proof-review--no-proofs');
        }
    }

    /* Edits Goes Here */

}

const updateDownloadUI = async (data) => {
    // Get the download section elements
    const projectNameElement = document.getElementById('projectName');
    const projectNumberElement = document.getElementById('projectNumber');
    const downloadButton = document.getElementById('downloadBtn');

    // Update project name
    if (projectNameElement && data.formData && data.formData.summary) {
        projectNameElement.textContent = data.formData.summary.projectName;
    }

    // Update project number
    if (projectNumberElement && data.order_name) {
        const orderNumber = data.order_name.includes('#') ?
            `${data.order_name.split('#')[1]}` :
            `${data.order_name}`;
        projectNumberElement.textContent = orderNumber;
    }

    // Update download button if there's an approved revision with files
    // Console log download url and name
    const projectName = data.formData?.summary?.projectName;
    if (downloadButton) {
        // Find source file for the current revision
        let sourceFileUrl = '';
        let isApproved = false;

        // Check if any of the revision files have isApproved set to true
        if (data.revisionFiles && Array.isArray(data.revisionFiles)) {
            // Find the source file for the current revision that is approved
            const sourceFile = data.revisionFiles.find(file =>
                file.revision_id === revisionId &&
                file.file_type === 'source' &&
                file.revisionData && file.revisionData.isApproved === true
            );

            if (sourceFile && sourceFile.url) {
                sourceFileUrl = sourceFile.url;
                isApproved = true;
            }
        }

        // Create the download button HTML
        const downloadButtonHtml = `
        <a
          href="${sourceFileUrl}"
          download="${projectName}"
          class="btn btn-primary btn-large c-pointer ${!isApproved ? 'disabled' : ''}"
          ${!isApproved ? 'disabled' : ''}
          >
          Download File
          <svg
            width="14"
            height="16"
            viewBox="0 0 14 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M1 10.4923L1 13.3608C1 13.7956 1.15804 14.2125 1.43934 14.5199C1.72064 14.8273 2.10218 15 2.5 15H11.5C11.8978 15 12.2794 14.8273 12.5607 14.5199C12.842 14.2125 13 13.7956 13 13.3608V10.4923M7.00084 1V10.2886M7.00084 10.2886L10.4294 6.73945M7.00084 10.2886L3.57227 6.73945"
              stroke="white"
              stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
        </a>
    `;
        downloadButton.innerHTML = downloadButtonHtml;
    }
}

// Function to update revision approval status
const updateProjectStatus = async (bundleId, revisionId, isApproved) => {
    try {
        const response = await fetch(updateApprovalUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                bundleId,
                revisionId,
                isApproved
            })
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response.json();

        return result;
    } catch (error) {

        throw error;
    }
};

// Helper function to send Klaviyo event via Firebase Function
const sendKlaviyoEvent = async (customerData, projectId, revisionId, projectData) => {
    try {
        // Extract customer information - ensure empty strings instead of undefined/null
        const customerEmail = customerData?.customer?.email || customerData?.email || '';
        const customerName = customerData?.customer ? 
            `${customerData?.customer?.firstName || ''} ${customerData?.customer?.lastName || ''}`.trim() : 
            customerData?.name || '';
        const customerPhone = customerData?.customer?.phone || customerData?.phone || '';
        
        if (!customerEmail) {
            return null;
        }

        // Get order_name directly from Firebase data
        const orderName = projectData?.order_name || '';
        
        // Get project name directly from Firebase data
        const projectName = projectData?.formData?.summary?.projectName || '';

        // Create the proof link URL using the current domain
        const currentDomain = window.location.origin;
        const proofLink = `${currentDomain}/pages/proof-review?bundleId=${projectId}&revisionId=${revisionId}` || '';
        
        // Send event to Klaviyo using Firebase Function
        const response = await fetch(sendKlaviyoEventUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                eventName: 'Proof Approved',
                customerEmail: customerEmail,
                customerName: customerName,
                customerPhone: customerPhone,
                orderName: orderName,
                projectName: projectName,
                projectId: projectId || '',
                revisionId: revisionId || '',
                proofLink: proofLink
            })
        });

        if (!response.ok) {
            throw new Error(`Firebase Function error! Status: ${response.status}`);
        }

        const result = await response.json();

        return result;
    } catch (error) {
        return null;
    }
};

// Handle the approval button click to update approval status
const handleApprovalButton = () => {
    // Get the elements
    const proofReviewSection = document.getElementById('proof-review');
    const downloadSection = document.getElementById('proof-review-download');
    const approveButton = document.querySelector('.proof-review__actions .btn-primary');

    // Add click handler to the approve button
    if (approveButton && proofReviewSection && downloadSection) {
        approveButton.addEventListener('click', async () => {
            // Show loading state
            approveButton.disabled = true;
            approveButton.innerHTML = 'Getting Ready...';

            try {
                // Update the approval status in Firestore
                if (bundleId && revisionId) {
                    const result = await updateProjectStatus(bundleId, revisionId, true);

                    // Refresh project data to get the updated approval status
                    const updatedData = await fetchProjectData();
                    
                    // Send Klaviyo event if we have customer data
                    if (updatedData && updatedData.shopify_order_id) {
                        try {
                            // Get customer data if not already available
                            let customerData = window.customerData; // Check if we already have customer data cached
                            
                            if (!customerData) {
                                // Fetch customer data if not already available
                                customerData = await fetchCustomerData(updatedData.shopify_order_id);
                                // Cache it for future use
                                window.customerData = customerData;
                            }
                            
                            if (customerData) {
                                // Send Klaviyo event with project data
                                sendKlaviyoEvent(customerData, bundleId, revisionId, updatedData)
                                    .then(result => {
                                        if (result) {
                                            // Success handling if needed
                                        }
                                    })
                                    .catch(error => {
                                        // Error handling if needed
                                    });
                            }
                        } catch (klaviyoError) {
                            // Log error but don't block the approval process

                        }
                    }

                    // Update the download UI with the latest data
                    updateDownloadUI(updatedData);

                    // Find the source file to download
                    let sourceFile = null;
                    if (updatedData.revisionFiles && Array.isArray(updatedData.revisionFiles)) {
                        sourceFile = updatedData.revisionFiles.find(file =>
                            file.revision_id === revisionId &&
                            file.file_type === 'source' &&
                            file.revisionData && file.revisionData.isApproved === true
                        );
                    }

                    // If we have a source file, trigger download automatically
                    /* if (sourceFile && sourceFile.url) {
                        // Create a temporary link element to trigger the download
                        const tempLink = document.createElement('a');
                        tempLink.href = sourceFile.url;
                        tempLink.download = updatedData.formData?.summary?.projectName || 'project';
                        document.body.appendChild(tempLink);
                        tempLink.click();
                        document.body.removeChild(tempLink);
                    } */
                }

                // The UI will be updated by updateProofReviewUI which is called when fetchProjectData returns
            } catch (error) {
                // Reset button state on error
                approveButton.disabled = false;
                approveButton.innerHTML = 'Approve';

                alert('Error approving the proof. Please try again.');
            }
        });
    }
}

// Drawer functionality
const initDrawer = () => {
    const openDrawerBtn = document.getElementById('openTipsDrawer');
    const drawer = document.getElementById('tipsDrawer');
    const drawerOverlay = drawer?.querySelector('.drawer__overlay');
    const closeDrawerBtn = drawer?.querySelector('.drawer__close-button');
    const drawerContainer = drawer?.querySelector('.drawer__container');

    if (!openDrawerBtn || !drawer || !drawerOverlay || !closeDrawerBtn || !drawerContainer) {

        return;
    }

    // Function to open the drawer
    const openDrawer = () => {
        drawer.classList.add('drawer--visible');
        document.body.style.overflow = 'hidden'; // Prevent body scrolling
    };

    // Function to close the drawer
    const closeDrawer = () => {
        drawer.classList.remove('drawer--visible');
        document.body.style.overflow = ''; // Restore body scrolling
    };

    // Event listeners
    openDrawerBtn.addEventListener('click', openDrawer);
    closeDrawerBtn.addEventListener('click', closeDrawer);
    drawerOverlay.addEventListener('click', closeDrawer);

    // Close on escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && drawer.classList.contains('drawer--visible')) {
            closeDrawer();
        }
    });

    // Prevent clicks inside the drawer from closing it
    drawerContainer.addEventListener('click', (e) => {
        e.stopPropagation();
    });
};

// Add event listeners for approval buttons
const addApprovalEventListeners = () => {
    // This function is a wrapper for handleApprovalButton to maintain consistency
    handleApprovalButton();
};

// Initialize the proof review functionality
const initProofReview = () => {
    // Check if already initialized
    if (window.proofReviewInitialized) return;
    
    // Set flag to prevent duplicate initialization
    window.proofReviewInitialized = true;

    // Fetch project data
    fetchProjectData();

    // Add event listeners for approval buttons
    addApprovalEventListeners();
};

// Initialize
document.addEventListener('DOMContentLoaded', () => {
    // Check if we're on the no-parameters view
    const noProofSection = document.getElementById('proof-review-no-proof');
    if (noProofSection) {
        // Hide the loading spinner immediately
        toggleLoadingSpinner(false);
        return; // Exit early, no need to fetch data
    }
    
    // Initialize drawer
    initDrawer();
    
    // Initialize the proof review functionality
    if (bundleId && revisionId) {
        initProofReview();
    } else {
        // If parameters are missing but we're not on the no-parameters view, hide spinner and show error
        toggleLoadingSpinner(false);
        showErrorMessage('Missing project or revision ID. Please check the URL and try again.');
    }
});